import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { useUser } from '../context/UserContext';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import Table from '../components/UI/Table';
import { Link } from 'react-router-dom';

const CheckpointManagement = () => {
    const [checkpoints, setCheckpoints] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);
    const { logout, hasPermission } = useUser();
	const canEdit = hasPermission("checkpoints", "edit");
	const canDelete = hasPermission("checkpoints", "delete");
	const canView = hasPermission("checkpoints", "view");
	const canCreate = hasPermission("checkpoints", "create");

    const { register, handleSubmit, reset, formState: { errors } } = useForm();

    useEffect(() => {
        fetchCheckpoints();
    }, []);

	if (!canView) {
		return <div className="text-center text-2xl text-red-500">У вас нет доступа к просмотру КПП</div>;
	}

    const fetchCheckpoints = async () => {
        try {
            const res = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/checkpoints`,
                { method: 'GET' },
                logout
            );
            const data = await res.json();
            setCheckpoints(data);
        } catch (error) {
            console.error('Ошибка загрузки КПП:', error);
        }
    };

    const handleStatusChange = async (id, currentStatus) => {
        try {
            const res = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/checkpoints/${id}`,
                {
                    method: 'PUT',
                    body: JSON.stringify({ isActive: !currentStatus }),
                },
                logout
            );
            if (res.ok) {
                fetchCheckpoints();
            }
        } catch (error) {
            console.error('Ошибка изменения статуса:', error);
        }
    };

    const onSubmit = async (data) => {
        try {
            const url = selectedCheckpoint
                ? `${process.env.REACT_APP_API_URL}/checkpoints/${selectedCheckpoint._id}`
                : `${process.env.REACT_APP_API_URL}/checkpoints`;

            const res = await fetchWithAuth(
                url,
                {
                    method: selectedCheckpoint ? 'PUT' : 'POST',
                    body: JSON.stringify(data),
                },
                logout
            );

            if (res.ok) {
                setIsModalOpen(false);
                reset();
                fetchCheckpoints();
            }
        } catch (error) {
            console.error('Ошибка сохранения КПП:', error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Вы уверены, что хотите удалить этот КПП?')) {
            try {
                const res = await fetchWithAuth(
                    `${process.env.REACT_APP_API_URL}/checkpoints/${id}`,
                    { method: 'DELETE' },
                    logout
                );
                if (res.ok) {
                    fetchCheckpoints();
                }
            } catch (error) {
                console.error('Ошибка удаления КПП:', error);
            }
        }
    };

    const columns = [
        { header: 'Название', accessor: 'name' },
        { header: 'Описание', accessor: 'description' },
        { header: 'Локация', accessor: 'location' },
        { header: 'Тип', accessor: 'type' },
        { 
            header: 'Статус', 
            accessor: 'isActive',
            render: (isActive, row) => (
                <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span>{isActive ? 'Активен' : 'Неактивен'}</span>
                </div>
            )
        },
        {
            header: 'Действия',
            accessor: '_id',
            render: (id, row) => (
                <div className="flex gap-2">
					{canEdit && <Button
						variant="edit"
						onClick={() => {
							const checkpoint = checkpoints.find(c => c._id === id);
							setSelectedCheckpoint(checkpoint);
							setIsModalOpen(true);
						}}
					>
						Редактировать
					</Button>}
                    {canEdit && <Button
                        variant={row.isActive ? "danger" : "edit"}
                        onClick={() => handleStatusChange(id, row.isActive)}
                    >
                        {row.isActive ? 'Деактивировать' : 'Активировать'}
                    </Button>}
                    {canDelete && <Button
                        variant="danger"
                        onClick={() => handleDelete(id)}
                    >
                        Удалить
                    </Button>}
                </div>
            ),
        },
    ];

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl text-[#CCCCCC] font-semibold">Управление КПП</h2>
                <div className="flex gap-2">
                    <Link
                        to="/checkpoints/active"
                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                    >
                        Активные КПП
                    </Link>
                    {canCreate && <Button
                        variant="primary"
                        onClick={() => {
                            setSelectedCheckpoint(null);
                            setIsModalOpen(true);
                        }}
                    >
                        Добавить КПП
                    </Button>}
                </div>
            </div>

            <Table data={checkpoints} columns={columns} />

            <Modal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    reset();
                    setSelectedCheckpoint(null);
                }}
                title={selectedCheckpoint ? 'Редактировать КПП' : 'Добавить КПП'}
            >
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <input
                            {...register('name', { required: true })}
                            placeholder="Название"
                            className="w-full bg-[#1E1E1E] text-white p-2 rounded border border-[#555]"
                            defaultValue={selectedCheckpoint?.name}
                        />
                        {errors.name && <p className="text-red-400">Обязательное поле</p>}
                    </div>

                    <input
                        {...register('description')}
                        placeholder="Описание"
                        className="w-full bg-[#1E1E1E] text-white p-2 rounded border border-[#555]"
                        defaultValue={selectedCheckpoint?.description}
                    />

                    <div>
                        <input
                            {...register('location', { required: true })}
                            placeholder="Локация"
                            className="w-full bg-[#1E1E1E] text-white p-2 rounded border border-[#555]"
                            defaultValue={selectedCheckpoint?.location}
                        />
                        {errors.location && <p className="text-red-400">Обязательное поле</p>}
                    </div>

                    <div>
                        <select
                            {...register('type', { required: true })}
                            className="w-full bg-[#1E1E1E] text-white p-2 rounded border border-[#555]"
                            defaultValue={selectedCheckpoint?.type}
                        >
                            <option value="">Выберите тип</option>
                            <option value="entry">Вход</option>
                            <option value="exit">Выход</option>
                        </select>
                        {errors.type && <p className="text-red-400">Обязательное поле</p>}
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => {
                                setIsModalOpen(false);
                                reset();
                                setSelectedCheckpoint(null);
                            }}
                        >
                            Отмена
                        </Button>
                        <Button type="submit" variant="primary">
                            Сохранить
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default CheckpointManagement; 